import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Link, MenuItem, Select, SelectChangeEvent, Switch, TextField } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridFilterModel,
  GridSortModel,
  getGridBooleanOperators,
} from '@mui/x-data-grid-pro';

import User, { AssessorLevel, AssessorProfile, AssessorReportingCompany, Company } from '../../interfaces/users.interface';
import { updateCompanyBoolean } from '../../services/company';
import { updateAssessorProfile, createAssessorProfile, createAssessorReportingCompany, fetchAssessorReportingCompanies } from '../../services/assessorProfiles';
import { getAssessorsForDataGrid } from '../../services/user';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchAssessorLevels } from '../../services/assessorLevels';
import BaseToolbar from '../Datagrid/BaseToolbar';
import GridPaginationBar from './GridPaginationBar';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';

interface AssessorRow {
  id: number;
  company?: Company;
  assessorLevel: AssessorLevel | null;
  'company__assessor_level__id': number;
  email: string;
  name: string;
  canAssessPrem: boolean;
  canAssessCloud: boolean;
  assessorLevelOptions?: AssessorLevel[];
  isActive: boolean;
}
const EMPTY_CELL = '-';

/** The fields of an assessor profile this grid edits, one cell at a time. */
type AssessorProfilePatch = Partial<Pick<AssessorProfile, 'hasZeroTrustCertification' | 'isActive' | 'reportingCompany'>>;

type SaveProfile = (assessorRow: AssessorRow, patch: AssessorProfilePatch) => void;

/** The admin-facing message for a failed request: the API's own complaint when it sends one. */
function requestErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  return data?.detail ?? fallback;
}

/**
 * Profile switches are CONTROLLED off row state, which is refreshed from each save's
 * response. An uncontrolled `defaultChecked` switch showed the pre-save value again
 * whenever the grid remounted its cells, so a save that worked looked like it had been
 * silently discarded (TPE-2955).
 */
function ztaAssessorRowSwitch(assessorRow: AssessorRow, saveProfile: SaveProfile) {
  const assessorProfile = assessorRow.company?.assessorProfile;
  if (!assessorProfile) return EMPTY_CELL;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    saveProfile(assessorRow, { hasZeroTrustCertification: event.target.checked });
  };
  return (
    <Switch
      id={assessorRow.id.toString()}
      checked={!!assessorProfile.hasZeroTrustCertification}
      onChange={(event) => handleChange(event)}
      name='ZT Accredited'
    />
  );
}

function isActiveSwitch(assessorRow: AssessorRow, saveProfile: SaveProfile) {
  const assessorProfile = assessorRow.company?.assessorProfile;
  if (!assessorProfile) return EMPTY_CELL;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    saveProfile(assessorRow, { isActive: event.target.checked });
  };
  return (
    <Switch
      id={assessorRow.id.toString()}
      checked={!!assessorProfile.isActive}
      onChange={(event) => handleChange(event)}
      name='Is Active'
    />
  );
}

function createRowSwitch(assessorRow: AssessorRow, field: string) {
  if (!assessorRow.company) return EMPTY_CELL;

  const handleChange = (event: any) => {
    updateCompanyBoolean({
      id: assessorRow.company!.id as number,
      [event.target.name]: event.target.checked,
    });
  };
  const fieldName: string = field as string;
  const fieldValue = assessorRow[fieldName as keyof typeof assessorRow];
  return (
    <Switch
      id={assessorRow.id.toString()}
      defaultChecked={typeof fieldValue === 'boolean' ? fieldValue : false}
      onChange={(event) => handleChange(event)}
      name={field}
    />
  );
}

function CustomToolbar(props: any) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <BaseToolbar
        {...props}
        csvOptions={{ fileName: `tpn_assessors_export_${new Date().toLocaleDateString()}` }}
      />
    </Box>
  );
}

function createAdminSelect(
  assessorRow: AssessorRow,
  currentId: number,
  items: { id: number; descriptor: string }[],
  selectIdPrefix: string,
  onChange: (selectedValue: number) => void,
) {
  const hasCurrent = currentId === 0 || items.some(o => o.id === currentId);
  const options = hasCurrent
    ? items
    : [{ id: currentId, descriptor: items.find(o => o.id === currentId)?.descriptor ?? `${selectIdPrefix} ${currentId}` }, ...items];

  return (
    <FormControl size='small'>
      <Select
        key={`${assessorRow.id}-${currentId}-${items.length}`}
        id={`${selectIdPrefix}-${assessorRow.id}`}
        defaultValue={currentId}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {options.map(opt => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.descriptor}
          </MenuItem>
        ))}
        <MenuItem value={0}>Null</MenuItem>
      </Select>
    </FormControl>
  );
}

function createLevelSelect(assessorRow: AssessorRow, assessorLevels: AssessorLevel[], gridValue: any) {
  const currentId = Number(gridValue ?? assessorRow.company__assessor_level__id ?? 0);
  const items = assessorLevels.map(l => ({ id: Number(l.id), descriptor: l.descriptor }));
  return createAdminSelect(assessorRow, currentId, items, 'assessor-level', (selectedValue) => {
    updateCompanyBoolean({
      id: assessorRow.company!.id as number,
      assessorLevel: selectedValue || null,
    });
  });
}

const ADD_NEW_SENTINEL = '__add_new__';

function ReportingCompanyCell({ assessorRow, reportingCompanies, onCompanyCreated, saveProfile, createProfile }: {
  assessorRow: AssessorRow;
  reportingCompanies: AssessorReportingCompany[];
  onCompanyCreated: (company: AssessorReportingCompany) => void;
  saveProfile: SaveProfile;
  createProfile: (assessorRow: AssessorRow, patch: AssessorProfilePatch) => void;
}) {
  // Read off row state (refreshed from each save) rather than a local copy, so the cell
  // shows what is actually stored.
  const selectedId = Number(assessorRow.company?.assessorProfile?.reportingCompany ?? 0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const items = reportingCompanies.map(c => ({ id: Number(c.id), descriptor: c.name }));
  const hasCurrent = selectedId === 0 || items.some(o => o.id === selectedId);
  const options = hasCurrent
    ? items
    : [{ id: selectedId, descriptor: items.find(o => o.id === selectedId)?.descriptor ?? `Company ${selectedId}` }, ...items];

  const saveReportingCompany = (value: number) => {
    const reportingCompany = value !== 0 ? value : null;
    if (assessorRow.company?.assessorProfile) {
      saveProfile(assessorRow, { reportingCompany });
    } else if (assessorRow.company?.id) {
      createProfile(assessorRow, { reportingCompany });
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<number | string>) => {
    if (event.target.value === ADD_NEW_SENTINEL) {
      setDialogOpen(true);
      return;
    }
    saveReportingCompany(Number(event.target.value));
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewName('');
    setError('');
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewName(event.target.value);
    setError('');
  };

  const handleConfirmNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      const newCompany = await createAssessorReportingCompany(trimmed);
      onCompanyCreated(newCompany);
      saveReportingCompany(newCompany.id);
      handleDialogClose();
    } catch {
      setError(`"${trimmed}" already exists or could not be created.`);
    }
  };


  return (
    <>
      <FormControl size='small'>
        <Select
          id={`reporting-company-${assessorRow.id}`}
          value={selectedId}
          onChange={(event) => handleSelectChange(event)}
        >
          {options.map(opt => (
            <MenuItem key={opt.id} value={opt.id}>{opt.descriptor}</MenuItem>
          ))}
          <MenuItem value={0}>Null</MenuItem>
          <MenuItem value={ADD_NEW_SENTINEL}>+ Add new company</MenuItem>
        </Select>
      </FormControl>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='xs' fullWidth>
        <DialogTitle>Add Reporting Company</DialogTitle>
        <DialogContent>
          <TextField
            label='Company name'
            value={newName}
            onChange={(event) => handleNameChange(event)}
            error={!!error}
            helperText={error}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmNew} variant='contained' disabled={!newName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


function createAssessorRow(assessor: User, assessorLevelOptions: AssessorLevel[] = []) {
  const company = assessor.companies?.[0];
  const rawLevel: any = company?.assessorLevel ?? null;

  const levelId = Number(
    rawLevel == null
      ? 0
      : typeof rawLevel === 'object'
        ? rawLevel.id
        : rawLevel,
  );

  const levelObj: AssessorLevel | null =
    rawLevel && typeof rawLevel === 'object'
      ? rawLevel
      : assessorLevelOptions.find(l => Number(l.id) === levelId) ?? null;

  const key = 'company__assessor_level__id' as const;

  const isActive = !!company?.assessorProfile?.isActive;

  if (!company) {
    return {
      id: assessor.id!,
      assessorLevel: null,
      email: assessor.email,
      [key]: 0,
      name: assessor.firstName + ' ' + assessor.lastName,
      canAssessPrem: false,
      canAssessCloud: false,
      assessorLevelOptions,
      isActive,
    };
  }

  return {
    id: assessor.id!,
    company,
    assessorLevel: levelObj,
    email: assessor.email,
    [key]: levelId,
    name: assessor.firstName + ' ' + assessor.lastName,
    canAssessPrem: !!company.canAssessPrem,
    canAssessCloud: !!company.canAssessCloud,
    assessorLevelOptions,
    isActive,
  };
}

export default function AssessorAdminPanel() {
  const [rows, setRows] = useState<AssessorRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'email', sort: 'asc' },
  ]);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [assessorLevels, setAssessorLevels] = useState<AssessorLevel[]>([]);
  const [reportingCompanies, setReportingCompanies] = useState<AssessorReportingCompany[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleCompanyCreated = useCallback((company: AssessorReportingCompany) => {
    setReportingCompanies(prev => [...prev, company]);
  }, []);

  const applyProfileToRow = useCallback((rowId: number, profile: AssessorProfile) => {
    setRows(prev => prev.map(row => (
      row.id === rowId && row.company
        ? { ...row, company: { ...row.company, assessorProfile: profile }, isActive: !!profile.isActive }
        : row
    )));
  }, []);

  // One changed field per request, and the row takes its new state from the response — so a
  // second edit on the same row can't carry a stale sibling field back over the server's copy.
  const saveProfile = useCallback(async (assessorRow: AssessorRow, patch: AssessorProfilePatch) => {
    const profileId = assessorRow.company?.assessorProfile?.id;
    if (!profileId) return;
    try {
      const saved = await updateAssessorProfile(profileId, patch);
      applyProfileToRow(assessorRow.id, saved);
      setActionError(null);
    } catch (error: any) {
      setActionError(requestErrorMessage(error, `Could not save ${assessorRow.email}'s assessor profile.`));
    }
  }, [applyProfileToRow]);

  const createProfile = useCallback(async (assessorRow: AssessorRow, patch: AssessorProfilePatch) => {
    const companyId = assessorRow.company?.id;
    if (!companyId) return;
    try {
      const created = await createAssessorProfile(companyId as number, patch);
      applyProfileToRow(assessorRow.id, created);
      setActionError(null);
    } catch (error: any) {
      setActionError(requestErrorMessage(error, `Could not create an assessor profile for ${assessorRow.email}.`));
    }
  }, [applyProfileToRow]);

  const columns: GridColDef[] = React.useMemo(() => {
    const assessorLevelValueOptions = [
      ...assessorLevels.map(l => ({ value: l.id, label: l.descriptor })),
      { value: 0, label: 'Null' },
    ];
    const assessorReportingCompanyOptions = [
      ...reportingCompanies.map(c => ({ value: c.id, label: c.name })),
      { value: 0, label: 'Null' },
    ];

    return [
      {
        field: 'company__assessor_level__id',
        headerName: 'Level',
        width: 300,
        valueOptions: assessorLevelValueOptions,
        valueFormatter: (params) => {
          const v = Number(params.value ?? 0);
          const match = assessorLevels.find(l => Number(l.id) === v);
          return match?.descriptor ?? 'Null';
        },
        renderCell: (params) => createLevelSelect(params.row, assessorLevels, params.value),
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 300,
        filterOperators: stringContainsOnly,
        renderCell: (params) => <Link href={`mailto:${params.row.email}`}>{params.row.email}</Link>,
      },
      {
        field: 'first_name',
        headerName: 'Name',
        filterOperators: stringContainsOnly,
        renderCell: (params) => params.row.name,
        width: 300,
      },
      {
        field: 'company__can_assess_prem',
        headerName: 'Can Assess Site',
        width: 150,
        renderCell: params => createRowSwitch(params.row, 'canAssessPrem'),
        filterOperators: getGridBooleanOperators(),
      },
      {
        field: 'company__can_assess_cloud',
        headerName: 'Can Assess Cloud',
        width: 150,
        renderCell: params => createRowSwitch(params.row, 'canAssessCloud'),
        filterOperators: getGridBooleanOperators(),
      },
      {
        field: 'company__assessor_profile__has_zero_trust_certification',
        headerName: 'ZT Accredited',
        width: 150,
        renderCell: params => ztaAssessorRowSwitch(params.row, saveProfile),
        filterOperators: getGridBooleanOperators(),
      },
      {
        field: 'company__assessor_profile__is_active',
        headerName: 'Is Active',
        width: 150,
        renderCell: params => isActiveSwitch(params.row, saveProfile),
        filterOperators: getGridBooleanOperators(),
      },
      {
        field: 'company__assessor_profile__reporting_company__name',
        headerName: 'Reporting Company',
        width: 150,
        valueOptions: assessorReportingCompanyOptions,
        valueFormatter: (params) => {
          const v = Number(params.value ?? 0);
          const match = reportingCompanies.find(c => Number(c.id) === v);
          return match?.name ?? 'Null';
        },
        renderCell: (params) => (
          <ReportingCompanyCell
            assessorRow={params.row}
            reportingCompanies={reportingCompanies}
            onCompanyCreated={handleCompanyCreated}
            saveProfile={saveProfile}
            createProfile={createProfile}
          />
        ),
      },
    ];
  }, [assessorLevels, reportingCompanies, handleCompanyCreated, saveProfile, createProfile]);

  const fetchAndSetRows = async () => {
    try {
      const data = await getAssessorsForDataGrid({
        page,
        pageSize,
        sortModel,
        filterModel: filterModelApplied,
      });

      setRows((data.results as User[]).map(u => createAssessorRow(u, assessorLevels)));
      setRowCount(data.count);
    } catch (e) {
      console.error('Error fetching assessors', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessorLevels().then(levels => setAssessorLevels(levels));
  }, []);

  useEffect(() => {
    fetchAssessorReportingCompanies().then(companies => setReportingCompanies(companies));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, sortModel, filterModelApplied, assessorLevels]);

  return (
    <Card sx={{ padding: '2em' }}>
      {actionError && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
      )}
      <DataGridPro
        style={{ height: 400, width: '100%' }}
        loading={isLoading}
        rows={rows}
        columns={columns}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100, 200]}
        rowCount={rowCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => { setPage(0); setSortModel(model); }}
        filterMode="server"
        filterModel={filterModelDraft}
        onFilterModelChange={(model) => {
          setPage(0);
          setFilterModelDraft(model);

          const normalized = normalizeFilterModel(model);
          if (!sameModel(normalized, filterModelApplied)) {
            setFilterModelApplied(normalized);
          }
        }}
        components={{
          Toolbar: CustomToolbar,
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
      />
    </Card>
  );
}
